import React, { useState } from 'react';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail, sendFirstAccessCode } from './auth-compat';
import { useNavigate } from 'react-router-dom';
import LoginIlustracao from './components/LoginIlustracao';
import { db, getCollectionName } from './firebase';
import { collection, query, where, getDocs, doc, updateDoc } from './firestore-compat';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(() =>
    new URLSearchParams(window.location.search).get('bloqueado') === '1'
      ? 'Seu acesso foi bloqueado. Procure o administrador do sistema.' : '');
  // null = tela de login; 'primeiro' = Primeiro acesso; 'recuperar' = Esqueci minha senha
  const [codeMode, setCodeMode] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);
  const navigate = useNavigate();

  // Hook de navegação se estiver usando react-router v6 (assumindo v6 pela importação do Link, mas o código original não tinha useNavigate)
  // Vou importar useNavigate para redirecionar caso precise trocar a senha.
  // Se o projeto for v5, seria useHistory. Pelo package.json (react-router-dom ^6...), é v6.
  // Mas espera, o Login original não tinha navegação explícita, ele dizia "// O App.js vai detetar o login".
  // Vou manter o padrão, mas preciso interceptar o fluxo se precisar mudar a senha.

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const auth = getAuth();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // histórico de acessos: login (o servidor guarda IP e aparelho)
      import('./utils/registrarAcesso').then(m => m.registrarAcesso({ tipo: 'login', caminho: '/login', tela: 'Login' })).catch(() => {});

      // Single-session: grava token no DB PRIMEIRO, só salva no localStorage
      // DEPOIS da confirmação do banco. Isso evita a race condition onde o
      // localStorage tinha token novo mas o DB ainda tinha o antigo.
      try {
        const sessionToken = crypto.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        // 1) Grava no banco de dados (obrigatório)
        const snap = await getDocs(
          query(collection(db, getCollectionName('users')), where('email', '==', email.toLowerCase()))
        );
        if (!snap.empty) {
          await updateDoc(doc(db, getCollectionName('users'), snap.docs[0].id), {
            sessionToken,
            sessionAt: new Date().toISOString(),
          });
          // 2) Só salva no localStorage DEPOIS do banco confirmar
          localStorage.setItem('qh_session_token', sessionToken);
        }
      } catch (e) {
        // Se falhar, o login continua mas sem single-session nesta sessão.
        // Não define localStorage → polling não expulsa ninguém.
        console.warn('[single-session] Falha ao gravar token:', e.message);
      }

    } catch (err) {
      // Tratamento de erros do Firebase
      if (err.code === 'auth/wrong-password') {
        setError('Senha incorreta.');
      } else if (err.code === 'auth/user-not-found') {
        setError('Usuário não encontrado.');
      } else if (err.code === 'auth/user-disabled') {
        setError('Acesso bloqueado. Procure o administrador do sistema.');
      } else if (err.code === 'auth/email-not-confirmed') {
        setError('E-mail não confirmado. Verifique se a configuração "Confirm email" está desativada no Supabase.');
      } else if (err.code === 'auth/email-logins-disabled') {
        setError('O provedor de e-mail está desativado no Supabase. Ative o "Email provider" nas configurações.');
      } else {
        setError('E-mail ou senha inválidos.');
      }
      console.error(err);
    }
  };

  const irParaTelaDoCodigo = (enviado) => {
    const params = new URLSearchParams({ email: email.trim().toLowerCase(), modo: codeMode });
    if (enviado) params.set('enviado', '1');
    navigate(`/nova-senha?${params}`);
  };

  // Envia o código e já leva para a tela de digitar o código (mesma aba)
  const handleSendCode = async () => {
    if (!email) {
      setError('Digite seu e-mail.');
      return;
    }

    setSendingReset(true);
    try {
      if (codeMode === 'primeiro') await sendFirstAccessCode(email.trim().toLowerCase());
      else await sendPasswordResetEmail(getAuth(), email.trim().toLowerCase());
      setError('');
      irParaTelaDoCodigo(true);
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/too-many-requests') {
        setError('Um código acabou de ser enviado. Aguarde 1 minuto antes de pedir outro.');
      } else {
        setError('Erro ao enviar o e-mail. Tente novamente em alguns minutos.');
      }
    } finally {
      setSendingReset(false);
    }
  };

  const linkStyle = { background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline', cursor: 'pointer', fontSize: 14, padding: 0 };

  return (
    <div className="login-tela">
      {/* Painel da marca (some no celular) */}
      <aside className="login-painel">
        <div className="login-painel-topo">
          <span className="login-painel-selo">Quality · Gestão Homecare</span>
          <h2>Tudo conectado, do primeiro contato ao cuidado em casa.</h2>
          <p>Captação, pacientes, equipes, indicadores e comunicação em um só lugar, com integração com outros sistemas.</p>
        </div>
        <LoginIlustracao />
        <ul className="login-painel-chips">
          <li>🔗 Integração com sistemas</li>
          <li>📊 Dados em tempo real</li>
          <li>💬 Comunicação entre setores</li>
        </ul>
      </aside>

      <main className="login-lado-form">
      <div className="login-box login-box-v2">
        <img className="login-logo" src="/marca/quality-logo-claro.png" alt="Quality — gestaoquality.com.br" />

        {!codeMode ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>E-mail (Usuário)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="error-message">{error}</p>}

            <button type="submit" className="btn-login">Entrar</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button type="button" onClick={() => { setError(''); setCodeMode('primeiro'); }} style={linkStyle}>
                Primeiro acesso
              </button>
              <button type="button" onClick={() => { setError(''); setCodeMode('recuperar'); }} style={linkStyle}>
                Esqueci minha senha
              </button>
            </div>
          </form>
        ) : (
          <div className="reset-password-section">
            <h3>{codeMode === 'primeiro' ? 'Primeiro acesso' : 'Recuperar senha'}</h3>
            <p style={{ fontSize: '0.9rem', marginBottom: '10px' }}>
              {codeMode === 'primeiro'
                ? 'Digite o e-mail cadastrado pelo administrador. Vamos enviar um código para você criar sua senha.'
                : 'Digite seu e-mail para receber um código para criar uma nova senha.'}
            </p>
            <div className="form-group">
              <label>E-mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
              />
            </div>
            {error && <p className="error-message">{error}</p>}

            <button onClick={handleSendCode} className="btn-login" disabled={sendingReset} style={{ marginBottom: '10px' }}>{sendingReset ? 'Enviando...' : 'Enviar código'}</button>
            <button
              onClick={() => { setError(''); setCodeMode(null); }}
              className="btn-secondary"
              style={{ background: 'none', border: '1px solid #ccc', padding: '8px', width: '100%', cursor: 'pointer' }}
            >
              Cancelar / Voltar
            </button>
            <button type="button" onClick={() => { if (!email) { setError('Digite seu e-mail.'); return; } irParaTelaDoCodigo(false); }} style={{ ...linkStyle, display: 'block', margin: '14px auto 0' }}>
              Já tenho um código
            </button>
          </div>
        )}

      </div>
      <p className="login-rodape">Versão 5.0 · Desenvolvido por Alexandre Oliveira - ADM Soluções Criativas 2025-2026 · Certificado INPI</p>
      </main>
    </div>
  );
}

export default Login;